import useAnalyticsCalls from '@/hooks/use-analytics';
import { getFunnelLead, useFunnelLead } from '@/hooks/useFunnelLead';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { FunnelChecklistCard } from '../components/FunnelChecklistCard';
import { FunnelField } from '../components/FunnelField';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath, useFunnelVariant } from '../routes';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function JoinStep1() {
  const variant = useFunnelVariant();
  const copy = FUNNEL_COPY[variant].step1;
  const navigate = useNavigate();
  const { updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  // Initialize email from the localStorage funnel-lead cache.
  // useFunnelLead.updateLead writes the full lead to localStorage on every
  // PATCH success, so it survives refreshes and step navigations. Reading
  // synchronously here (in the useState initializer) means we never need a
  // useEffect to sync from the server's lead response — and we avoid the
  // race where a freshly-minted funnel-session triggers a server lead
  // fetch that overwrites what the user just typed.
  const [email, setEmail] = useState<string>(() => getFunnelLead()?.email ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Onboarding - Started (Meta view-002 per Tracking Requirements) fires on
  // first mount only. The per-step page view is handled globally by
  // useAnalyticsPageViews, which now attaches `positioning_variant` and
  // `funnel_step` to `[Amplitude] Page Viewed` for /join routes.
  const hasFiredOnceRef = useRef(false);
  useEffect(() => {
    if (hasFiredOnceRef.current) return;
    hasFiredOnceRef.current = true;
    analytics.callOnboardingStarted();
  }, [analytics]);

  const handleEmailBlur = async (value: string) => {
    if (!value) return;
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    // Attach email + user_type='lead' as Amplitude user properties BEFORE
    // the Funnel - Field Captured track event below. Iterable's
    // Amplitude-source destination maps leads by the email user property
    // (and uses first_name/last_name for personalization when those
    // fields ship — wired through the same helper); without this, the
    // funnel event lands anonymous and Iterable can't create a contact.
    // We deliberately do NOT setUserId — that gets the canonical member
    // UUID after payment confirms (callIdentifyMembership). Setting
    // user_id=email at funnel time would split the same person into two
    // distinct users in Amplitude.
    analytics.callIdentifyFunnelLead({ email: value });
    try {
      await updateLead({ email: value });
      analytics.callFunnelFieldCaptured({ field: 'email', value, positioningVariant: variant });
    } catch {
      // Best-effort; FunnelGuard surfaces the failure and lazy-mint retries.
    }
  };

  const isComplete = email.trim() && !emailError;

  const handleContinue = () => {
    if (!isComplete) return;
    navigate(stepRoutePath(variant, 2));
  };

  return (
    <JoinFunnelLayout step={1} onContinue={handleContinue} isContinueDisabled={!isComplete}>
      <div className="flex flex-col gap-3">
        <h1 className="text-catch-dark-blue-90 text-3xl leading-tight font-bold">{copy.header}</h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <FunnelChecklistCard sections={copy.checklist} />

      <div className="flex flex-col gap-4">
        <FunnelField
          id="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          onBlur={(e) => handleEmailBlur(e.target.value.trim())}
          autoComplete="email"
          error={emailError}
        />
      </div>
    </JoinFunnelLayout>
  );
}

export default JoinStep1;
